package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

data class ComplianceBox(
    val badge: String,
    val heading: String,
    val intro: String,
    val facts: List<Pair<String, String>>
)

// ─────────────────────────────────────────
// PERSONALLIGGARE (/personalliggare)
// ─────────────────────────────────────────
@Composable
fun PersonalliggarePage(onSignUp: () -> Unit, onLogin: () -> Unit, onNavigate: (String) -> Unit, onPrivacy: () -> Unit = {}, onTerms: () -> Unit = {}, onDpa: () -> Unit = {}) {
    featurePage(
        path = "/personalliggare",
        eyebrow = "Skatteverkets krav • SKVFS 2015:6",
        h1 = "Elektronisk personalliggare för restaurang, café & pizzeria – godkänd enligt Skatteverket",
        lead = "Skatteverket kräver att restauranger, caféer, pizzerior, bagerier och barer för elektronisk personalliggare. Med Mittpass checkar personalen in och ut via mobilen – revisionssäkert, utan dyr hårdvara och redo för oanmält kontrollbesök på ett klick.",
        showcaseImage = "/screen_personalliggare.png",
        showcaseAlt = "Mittpass Elektronisk Personalliggare – Skatteverket-godkänd mobil vy",
        benefits = listOf(
            "fa-solid fa-clock" to "Mobil in- och utstämpling i realtid" to "Personalen stämplar in och ut direkt i Mittpass-appen för iOS och Android när passet startar och slutar.",
            "fa-solid fa-shield-halved" to "Oföränderlig revisionslogg (SKVFS 2015:6)" to "Alla registreringar och eventuella efterhandskorrigeringar sparas med full spårbarhet över vem som ändrat, när och varför.",
            "fa-solid fa-file-csv" to "1-klicks Skatteverket Inspektionsvy & CSV/PDF" to "Vid oanmält kontrollbesök från Skatteverket visar du personalliggaren direkt på skärmen eller exporterar filen med ett klick.",
            "fa-solid fa-qrcode" to "QR-kod & GPS-verifiering på plats" to "Säkerställ att incheckning sker fysiskt på arbetsplatsen via utskrivbar QR-kod eller platsverifiering.",
            "fa-solid fa-file-invoice-dollar" to "Automatiskt löneunderlag & OB" to "Varje stämpling kopplas direkt till schemat och räknar automatiskt ut arbetade timmar, raster, OB och Fortnox-underlag.",
            "fa-solid fa-mobile-screen-button" to "Ingen hårdvara eller kortläsare" to "Slipp väggmonterade stämpelklockor och dyra kassa-tillval. Fungerar på personalens egna telefoner eller en surfplatta."
        ),
        compliance = ComplianceBox(
            badge = "Lagkrav & Regler",
            heading = "Vad kräver Skatteverket av en digital personalliggare?",
            intro = "Enligt Skatteförfarandelagen (2011:1244) och Skatteverkets föreskrifter (SKVFS 2015:6) måste alla näringsidkare inom restaurang-, café-, pizzeria- och cateringbranschen löpande registrera samtliga verksamma personer i en elektronisk personalliggare.",
            facts = listOf(
                "12 500 kr i kontrollavgift" to "Om personalliggare saknas eller inte hålls omedelbart tillgänglig vid Skatteverkets oanmälda besök tas en kontrollavgift på 12 500 kr ut (25 000 kr vid upprepad brist inom ett år) plus 2 500 kr per oregistrerad person.",
                "Obligatoriska uppgifter" to "Systemet registrerar automatiskt företagets namn, organisationsnummer, arbetsplatsens namn samt varje arbetares fullständiga namn, personnummer/samordningsnummer och exakta in-/utcheckningstider.",
                "Minst 2 års säker arkivering" to "Mittpass lagrar samtliga stämplingar och den kryptografiskt spårbara ändringsloggen i molnet i minst 2 år efter beskattningsårets utgång enligt lag.",
                "Gäller all personal i lokalen" to "Kravet omfattar fast anställda, timanställda, extrapersonal, inhyrd personal, praktikanter samt oavlönade familjemedlemmar som utför arbete."
            )
        ),
        faqs = listOf(
            "Vad är en elektronisk personalliggare?" to "En elektronisk personalliggare är ett digitalt register som i realtid loggar vilka personer som arbetar i verksamhetslokalen. Mittpass uppfyller Skatteverkets föreskrifter (SKVFS 2015:6) med oföränderlig historik och omedelbar export.",
            "Vem måste ha personalliggare enligt lag?" to "Alla verksamheter inom restaurang, pizzeria, café, bageri, bar, catering och gatukök måste enligt Skatteförfarandelagen föra personalliggare.",
            "Vad blir böterna (kontrollavgiften) om personalliggaren saknas?" to "Skatteverket utfärdar en kontrollavgift på 12 500 kr om personalliggaren saknas eller är bristfällig vid kontroll (25 000 kr vid upprepning inom ett år), samt 2 500 kr för varje verksam person som inte är incheckad.",
            "Hur visar jag personalliggaren när Skatteverket kommer på kontrollbesök?" to "I Mittpass öppnar du fliken Personalliggare (eller aktiverar 1-tap Kontrollbesöksläge i appen) och visar dagens och historiska pass direkt, eller klickar på Exportera CSV/PDF för att lämna över underlaget till kontrollanten.",
            "Behöver vi köpa en stämpelklocka eller kassasystem-koppling?" to "Nej. Mittpass fungerar helt fristående i personalens egna smartphones (iOS och Android) eller på en gemensam surfplatta/dator på arbetsplatsen.",
            "Vad kostar Mittpass personalliggare?" to "Mittpass kostar endast 39 kr per aktiv användare och månad. I priset ingår både elektronisk personalliggare, schemaläggning, löneunderlag med OB och digital egenkontroll (HACCP). Ingen fast månadsavgift eller bindningstid."
        ),
        ctaText = "Testa personalliggaren gratis i 30 dagar",
        onSignUp = onSignUp,
        onLogin = onLogin,
        onNavigate = onNavigate,
        onPrivacy = onPrivacy,
        onTerms = onTerms,
        onDpa = onDpa
    )
}

// ─────────────────────────────────────────
// SCHEMALÄGGNING (/schemalagga-personal & /schemalaggning)
// ─────────────────────────────────────────
@Composable
fun SchemalaggningSida(onSignUp: () -> Unit, onLogin: () -> Unit, onNavigate: (String) -> Unit, onPrivacy: () -> Unit = {}, onTerms: () -> Unit = {}, onDpa: () -> Unit = {}) {
    featurePage(
        path = "/schemalagga-personal",
        eyebrow = "Smart schemaläggning • Restaurang & Café",
        h1 = "Schemaläggning för restaurang, café & pizzeria – bygg scheman på minuter",
        lead = "Slipp Excel-ark, SMS-kaos och sena luckor i bemanningen. Med Mittpass lägger du vecko- och månadsscheman med drag-and-drop, skickar ut lediga arbetspass via push-notis och får färdigt löneunderlag med automatisk OB.",
        showcaseImage = "/screen_schema_week.png",
        showcaseAlt = "Mittpass Schemaläggning – Vecko- och månadsvy för restaurang och café",
        benefits = listOf(
            "fa-solid fa-calendar-plus" to "Vecko-, månads- och årsvy" to "Överblicka hela bemanningen i visuell kalendervy eller Gantt-tidslinje. Anpassa dygnsfönstret efter dina öppettider.",
            "fa-solid fa-bell" to "Lediga pass med push-notis" to "Behöver du täcka ett pass snabbt? Publicera ett öppet pass så får personalen push-notis och kan ta passet med ett tryck.",
            "fa-solid fa-arrows-rotate" to "Smidiga vaktbyten & frånvaro" to "Medarbetare kan byta pass med kollegor och ansöka om ledighet eller semester i appen – du godkänner med ett klick.",
            "fa-solid fa-users" to "Realtidsradar – vem är på plats?" to "Se i realtid vilka som checkat in i personalliggaren, hur länge de arbetat och om någon är sen till sitt skift.",
            "fa-solid fa-calculator" to "Automatisk OB & dygnsvilokontroll" to "Systemet räknar ut OB-tillägg enligt kollektivavtal (HRF/Visita) och hjälper dig hålla koll på 11 timmars dygnsvila.",
            "fa-solid fa-file-export" to "Direkt till Fortnox & löneunderlag" to "Schemalagd och faktiskt stämplad tid sammanställs automatiskt vid månadsslut för export till Fortnox Lön eller CSV."
        ),
        compliance = ComplianceBox(
            badge = "Arbetstidslagen & Kollektivavtal",
            heading = "Schemaläggning anpassad för svenska regler, dygnsvila och OB",
            intro = "Inom restaurang, café och besöksnäring varierar bemanningen kraftigt mellan lunchrusch, kvällsservering och helger. Mittpass automatiserar regelverket så att du undviker dyra schemamissar.",
            facts = listOf(
                "11 timmars dygnsvila" to "Enligt Arbetstidslagen (1982:673) och EU:s arbetstidsdirektiv ska varje medarbetare ha minst 11 timmars sammanhängande ledighet per 24-timmarsperiod.",
                "36 timmars veckovila" to "Säkerställ att både fast personal och timanställda får sin lagstadgade veckovila även vid intensiva helg- och säsongspass.",
                "Automatisk OB (HRF / Visita)" to "Mittpass beräknar kvälls-OB, natt-OB och helg-OB automatiskt utifrån stämplade arbetspass och genererar korrekta lönerader.",
                "Allt-i-ett med personalliggare" to "När personalen stämplar in på sitt schemalagda pass uppdateras den Skatteverket-godkända personalliggaren automatiskt i bakgrunden."
            )
        ),
        faqs = listOf(
            "Hur fungerar schemaläggning i Mittpass?" to "Du bygger schemat i vecko- eller månadsvyn på webben eller i appen. När du publicerar ett arbetspass ser medarbetaren det direkt i sin mobilkalender och får en push-notis.",
            "Hur fungerar öppna pass när någon blir sjuk?" to "Om du har en lucka i schemat markerar du passet som öppet. Alla tillgängliga medarbetare får en push-notis i mobilen och kan ansöka om passet direkt. Du väljer vem som får passet med ett klick.",
            "Kan personalen byta pass med varandra i appen?" to "Ja. I vaktbytesmodulen kan en anställd föreslå ett passbyte med en kollega. När båda accepterat skickas bytet till dig som chef för slutgiltigt godkännande.",
            "Räknar systemet ut OB och löneunderlag automatiskt?" to "Ja. Mittpass matchar schemalagda pass mot faktisk in- och utstämpling, drar av raster och beräknar OB-ersättning samt semesterersättning redo för lönekörning eller Fortnox.",
            "Fungerar Mittpass för verksamheter med flera restauranger eller caféer?" to "Ja, med stöd för flera arbetsplatser (multi-workplace) kan du hantera separata scheman och personalstyrkor för varje enhet under samma konto.",
            "Vad kostar schemaläggning i Mittpass?" to "39 kr per aktiv användare och månad. Då ingår både schemaläggning, elektronisk personalliggare, löneunderlag och digital egenkontroll (HACCP). Prova gratis i 30 dagar utan betalkort."
        ),
        ctaText = "Testa schemaläggning gratis i 30 dagar",
        onSignUp = onSignUp,
        onLogin = onLogin,
        onNavigate = onNavigate,
        onPrivacy = onPrivacy,
        onTerms = onTerms,
        onDpa = onDpa
    )
}

// ─────────────────────────────────────────
// EGENKONTROLL (/egenkontroll)
// ─────────────────────────────────────────
@Composable
fun EgenkontrollSida(onSignUp: () -> Unit, onLogin: () -> Unit, onNavigate: (String) -> Unit, onPrivacy: () -> Unit = {}, onTerms: () -> Unit = {}, onDpa: () -> Unit = {}) {
    featurePage(
        path = "/egenkontroll",
        eyebrow = "Digital Egenkontroll • Restaurang, Café & Kök",
        h1 = "Digital egenkontroll för restaurang & café – slipp papperspärmarna i köket",
        lead = "Ersätt borttappade papperslistor med ett modernt digitalt egenkontrollprogram i mobilen. Logga dagliga kyl- och frystemperaturer, bocka av städ- och hygienronder på sekunder och ha alltid komplett dokumentation redo för miljöförvaltningen.",
        showcaseImage = "/screen_egenkontroll.png",
        showcaseAlt = "Mittpass Digital Egenkontroll – Checklistor och temperaturloggar i mobilen",
        benefits = listOf(
            "fa-solid fa-clipboard-check" to "Digitala checklistor & hygienronder" to "Färdiga och anpassningsbara kontrollpunkter för öppning, stängning, rengöring, varumottagning och skadedjurskontroll.",
            "fa-solid fa-temperature-half" to "Snabb temperaturloggning" to "Registrera temperaturer för samtliga kylar, frysar, varmhållning och nedkylning direkt i mobilen med visuella säkerhetszoner.",
            "fa-solid fa-triangle-exclamation" to "Automatiska avvikelselarm" to "Om ett värde överskrider gränsvärdet varnar appen direkt och guidar personalen att dokumentera korrigerande åtgärd.",
            "fa-solid fa-file-pdf" to "1-klicks Miljö & Hälsa Inspektionsrapport" to "Exportera ett komplett, tidsstämplat PDF- eller CSV-protokoll över alla utförda egenkontroller när inspektören kommer.",
            "fa-solid fa-eye" to "Realtidsöversikt för kökschef & ägare" to "Se direkt i dashboarden om dagens temperaturkontroller och städrutiner är utförda – även när du inte är på plats.",
            "fa-solid fa-layer-group" to "Samma app som schema & personalliggare" to "Personalen behöver bara en enda app. När de stämplar in på sitt arbetspass påminns de om dagens egenkontroller."
        ),
        compliance = ComplianceBox(
            badge = "Livsmedelsverkets Krav",
            heading = "Egenkontrollprogram som uppfyller svensk och europeisk livsmedelslagstiftning",
            intro = "Enligt EG-förordning 852/2004 och Livsmedelslagen (2006:804) är alla livsmedelsföretagare skyldiga att bedriva systematisk egenkontroll anpassad efter verksamhetens risker.",
            facts = listOf(
                "Slipp dyra uppföljningsavgifter" to "Om egenkontrollen saknar dokumentation vid kommunens inspektion debiteras extra timavgift för uppföljande kontroll och i allvarliga fall sanktionsavgift.",
                "Tidsstämplad signering" to "Varje kontrollpunkt och temperaturmätning i Mittpass loggas automatiskt med datum, klockslag och vilken medarbetare som utfört kontrollen.",
                "Korrigerande åtgärder" to "Lagstiftningen kräver inte bara att du mäter, utan att du dokumenterar vad som gjordes när en kyl var för varm. Mittpass har inbyggt avvikelseflöde.",
                "Ingår utan extra kostnad" to "Andra fristående egenkontrollappar kostar ofta 400–1 200 kr/mån. I Mittpass ingår hela egenkontrollmodulen i priset 39 kr/användare."
            )
        ),
        faqs = listOf(
            "Vad är ett egenkontrollprogram för restaurang och café?" to "Ett egenkontrollprogram är verksamhetens dokumenterade rutiner för att säkerställa säker mat – exempelvis temperaturmätning i kylar/frysar, varumottagning, rengöring, personlig hygien och allergenhantering.",
            "Är digital egenkontroll i mobilen godkänd av kommunen och Livsmedelsverket?" to "Ja. Miljö- och hälsoskyddsnämnden och Livsmedelsverket godkänner och uppmuntrar digital egenkontroll, förutsatt att historik och avvikelser kan visas upp vid tillsynsbesök.",
            "Hur lång tid tar det att sätta upp egenkontrollen i Mittpass?" to "Under två minuter. Du lägger till dina kyl- och frysenheter samt dagliga eller veckovisa kontrollpunkter, varefter personalen direkt kan börja logga i appen.",
            "Vad händer om en kyl är för varm när personalen loggar?" to "Mittpass markerar mätningen som en avvikelse i rött och ber medarbetaren ange vilken korrigerande åtgärd som vidtagits (t.ex. justerat termostat eller flyttat livsmedel).",
            "Kan jag skriva ut eller mejla egenkontrollen till miljöinspektören?" to "Ja, med ett klick genererar du en samlad inspektionsrapport för valfri tidsperiod som kan visas på skärmen, skrivas ut eller mejlas som PDF/CSV.",
            "Kostar egenkontrollmodulen något extra?" to "Nej. Allt i Mittpass – egenkontroll, HACCP, personalliggare, schemaläggning och löneunderlag – ingår för 39 kr per aktiv användare och månad."
        ),
        ctaText = "Digitalisera din egenkontroll gratis i 30 dagar",
        onSignUp = onSignUp,
        onLogin = onLogin,
        onNavigate = onNavigate,
        onPrivacy = onPrivacy,
        onTerms = onTerms,
        onDpa = onDpa
    )
}

// ─────────────────────────────────────────
// LIVSMEDELSKONTROLL (/livsmedelskontroll)
// ─────────────────────────────────────────
@Composable
fun LivsmedelskontrollSida(onSignUp: () -> Unit, onLogin: () -> Unit, onNavigate: (String) -> Unit, onPrivacy: () -> Unit = {}, onTerms: () -> Unit = {}, onDpa: () -> Unit = {}) {
    featurePage(
        path = "/livsmedelskontroll",
        eyebrow = "Kommunal tillsyn • Miljö & Hälsa",
        h1 = "Livsmedelskontroll för restaurang & café – klarar ni en oanmäld inspektion idag?",
        lead = "Kommunens miljö- och hälsoskyddsinspektörer gör oanmälda livsmedelskontroller året runt. Med Mittpass är dina temperaturloggar, hygienronder och avvikelserapporter alltid ifyllda, spårbara och redo att visas upp på 10 sekunder.",
        showcaseImage = "/screen_inspektionsrapport.png",
        showcaseAlt = "Mittpass Inspektionsrapport för kommunal livsmedelskontroll",
        benefits = listOf(
            "fa-solid fa-file-shield" to "Officiellt Miljö & Hälsa Inspektionsläge" to "Ta fram en dedikerad inspektionsvy på mobilen, surfplattan eller datorn som visar alla kontrollpunkter och temperaturserier för inspektören.",
            "fa-solid fa-bell-concierge" to "Inga glömda dagar i pärmen" to "Den vanligaste anmärkningen vid livsmedelskontroll är luckor i pappersloggarna. Mittpass visar tydligt dagens status så att inget missas.",
            "fa-solid fa-temperature-arrow-down" to "Full historik för kyl, frys & varmhållning" to "Visa obruten temperaturkedja från varumottagning och kylförvaring till tillagning, varmhållning och nedkylning.",
            "fa-solid fa-clipboard-list" to "Dokumenterade avvikelser & åtgärder" to "Inspektören granskar hur ni hanterar fel. I Mittpass syns varje temperaturavvikelse tillsammans med vidtagen åtgärd och signatur.",
            "fa-solid fa-broom" to "Rengöring, allergener & spårbarhet" to "Skapa strukturerade rutiner för städscheman, allergeninformaton och varumottagning anpassade efter er riskklassning.",
            "fa-solid fa-coins" to "Lägre risk och färre extra kontrolltimmar" to "Verksamheter med god efterlevnad slipper dyra uppföljande inspektioner (ofta 1 400–1 800 kr/timme) och får bättre kontrollfrekvens."
        ),
        compliance = ComplianceBox(
            badge = "Inspektion & Tillsyn",
            heading = "Så granskar kommunen din verksamhet vid en livsmedelskontroll",
            intro = "Livsmedelskontrollen utförs av kommunens miljöförvaltning (eller Livsmedelsverket) enligt Livsmedelslagen (2006:804). Kontrollerna sker nästan alltid oanmält och fokuserar på att dina rutiner fungerar i praktiken.",
            facts = listOf(
                "Vad inspektören begär först" to "Temperaturjournaler för kylar och frysar, dokumentation av nedkylning/varmhållning, städ- och rengöringsrutiner samt hur avvikelser har hanterats.",
                "Sanktionsavgifter & förelägganden" to "Vid allvarliga brister eller utebliven registrering kan sanktionsavgift på 5 000 kr upp till 1 % av årsomsättningen utfärdas, samt föreläggande med vite.",
                "Uppföljande kontroll kostar per timme" to "Om du får avvikelser vid ordinarie kontroll bokar kommunen en extra uppföljande kontroll där du debiteras kommunens timtaxa för allt merarbete.",
                "Digitalt protokoll på 1 klick" to "Med Mittpass slipper du leta efter pärmar i köket – exportera en komplett inspektionsrapport för de senaste 30, 90 eller 365 dagarna direkt."
            )
        ),
        faqs = listOf(
            "Hur går en livsmedelskontroll till på en restaurang eller ett café?" to "En livsmedelsinspektör från kommunens miljöförvaltning kommer på ett oanmält besök, inspekterar kök och lokaler, kontrollerar temperaturer i kylar/frysar och ber att få se dokumentationen för er egenkontroll och HACCP.",
            "Vilka är de vanligaste bristerna vid en livsmedelskontroll?" to "De vanligaste bristerna är ofullständiga temperaturloggar (glömda dagar/veckor i pärmen), odokumenterad nedkylning av mat, bristande städrutiner och att personalen inte dokumenterat vilken åtgärd som vidtagits vid felaktig temperatur.",
            "Vad kostar en extra livsmedelskontroll om vi får anmärkning?" to "Om miljöförvaltningen måste göra en uppföljande kontroll på grund av brister debiterar kommunen en timavgift (vanligen 1 400–1 850 kr per timme) för både inspektionstid och för- och efterarbete.",
            "Hur hjälper Mittpass oss att klara livsmedelskontrollen utan anmärkning?" to "Mittpass ger kökspersonal och chefer en enkel daglig checklista och temperaturlogg i mobilen med tydlig progressindikator, automatiska varningar vid gränsvärdesbrott och ett färdigt inspektionsprotokoll.",
            "Vad är skillnaden mellan livsmedelskontroll och egenkontroll?" to "Egenkontroll är restaurangens eget dagliga säkerhetsarbete och dokumentation (HACCP). Livsmedelskontroll är myndighetens (kommunens) inspektion som kontrollerar att egenkontrollen följs.",
            "Ingår funktionerna för livsmedelskontroll i vanliga Mittpass?" to "Ja, allt ingår för 39 kr per aktiv användare och månad, inklusive 30 dagars fri testperiod."
        ),
        ctaText = "Bli redo för nästa livsmedelskontroll – testa 30 dagar gratis",
        onSignUp = onSignUp,
        onLogin = onLogin,
        onNavigate = onNavigate,
        onPrivacy = onPrivacy,
        onTerms = onTerms,
        onDpa = onDpa
    )
}

// ─────────────────────────────────────────
// HACCP (/haccp)
// ─────────────────────────────────────────
@Composable
fun HaccpSida(onSignUp: () -> Unit, onLogin: () -> Unit, onNavigate: (String) -> Unit, onPrivacy: () -> Unit = {}, onTerms: () -> Unit = {}, onDpa: () -> Unit = {}) {
    featurePage(
        path = "/haccp",
        eyebrow = "HACCP & Kritiska Styrpunkter (CCP)",
        h1 = "Digitalt HACCP-system & temperaturlogg för restaurang, storkök och café",
        lead = "Bygg ett vattentätt HACCP-flöde utan pappersarbete. Övervaka kritiska styrpunkter (CCP), logga kyl-, frys-, nedkylnings- och varmhållningstemperaturer i mobilen och få omedelbara larm om gränsvärden överskrids.",
        showcaseImage = "/screen_egenkontroll.png",
        showcaseAlt = "Mittpass Digitalt HACCP-system och temperaturloggning",
        benefits = listOf(
            "fa-solid fa-temperature-low" to "Kritiska temperaturgränser (CCP)" to "Konfigurera exakta gränsvärden för varje kyl (≤ +4/+8 °C), frys (≤ -18 °C), varmbuffé (≥ +60 °C) och varumottagning.",
            "fa-solid fa-gauge-high" to "Visuell HACCP-status i realtid" to "Se direkt i appens progressring hur många av dagens HACCP-kontroller som är klara och om någon punkt kräver åtgärd.",
            "fa-solid fa-Notes-medical" to "7 HACCP-principer i praktiken" to "Från faroanalys och kritiska gränsvärden till övervakning, korrigerande åtgärder, verifiering och dokumentation.",
            "fa-solid fa-bolt" to "10 sekunders daglig loggning" to "Personalen drar temperaturreglaget eller skriver in gradantalet direkt på telefonen under morgon- och kvällsronden.",
            "fa-solid fa-rotate-left" to "Korrigerande åtgärder med historik" to "När en mätning avviker från gränsvärdet tvingar systemet fram en dokumenterad korrigerande åtgärd enligt HACCP-princip 5.",
            "fa-solid fa-file-circle-check" to "Revisionssäker HACCP-dokumentation" to "All data sparas molnbaserat med användarsignatur och tidsstämpel enligt HACCP-princip 7 – redo för revision och tillsyn."
        ),
        compliance = ComplianceBox(
            badge = "HACCP-Standard & Gränsvärden",
            heading = "De 7 HACCP-principerna och kritiska temperaturgränser i köket",
            intro = "HACCP (Hazard Analysis and Critical Control Points) är den internationella och EU-lagstadgade standarden (EG 852/2004, Artikel 5) för att identifiera, bedöma och kontrollera biologiska, kemiska och fysikaliska faror i livsmedelshantering.",
            facts = listOf(
                "Kylförvaring (≤ +4 °C till +8 °C)" to "Kylvaror ska hållas vid högst +8 °C (känsliga varor som köttfärs ≤ +4 °C och färsk fisk ≤ +2 °C) för att stoppa bakterietillväxt.",
                "Frysförvaring (≤ -18 °C)" to "Frysvaror ska förvaras vid -18 °C eller kallare. Mittpass varnar direkt om en frys börjar stiga i temperatur.",
                "Varmhållning (≥ +60 °C) & Tillagning (≥ +72 °C)" to "Vid varmhållning på buffé eller i värmeskåp får temperaturen aldrig understiga +60 °C.",
                "Nedkylning (+60 °C till +8 °C inom 4h)" to "Tillagad mat som ska kylas ned är en av kökets viktigaste kritiska styrpunkter (CCP) och måste dokumenteras noggrant."
            )
        ),
        faqs = listOf(
            "Vad betyder HACCP?" to "HACCP står för Hazard Analysis and Critical Control Points (Faroanalys och Kritiska Styrpunkter). Det är en systematisk metod i 7 steg för att förebygga hälsorisker vid matlagning och livsmedelshantering.",
            "Vilka är de 7 principerna i HACCP?" to "1) Gör en faroanalys, 2) Identifiera kritiska styrpunkter (CCP), 3) Fastställ kritiska gränsvärden, 4) Upprätta rutiner för övervakning, 5) Fastställ korrigerande åtgärder vid avvikelser, 6) Verifiera att systemet fungerar, 7) Dokumentera och arkivera alla kontroller.",
            "Vilka temperaturgränser gäller enligt HACCP i restaurangkök?" to "Riktvärdena är: Kylskåp högst +4 °C till +8 °C (beroende på råvara), Frys -18 °C eller kallare, Varmhållning minst +60 °C, Tillagning minst +70/+72 °C kärntemperatur, och Nedkylning från +60 °C till +8 °C på max 4 timmar.",
            "Behöver ett litet café eller en pizzeria verkligen HACCP?" to "Ja, alla verksamheter som säljer eller serverar livsmedel omfattas av kravet på HACCP-anpassade rutiner enligt EG-förordning 852/2004, men omfattningen anpassas efter verksamhetens storlek och risk.",
            "Hur ersätter Mittpass vår gamla HACCP-pärm?" to "I Mittpass lägger du in era kylar, frysar och kritiska kontrollpunkter digitalt. Personalen loggar mätvärdena i mobilen, får larm vid avvikelser och ni kan när som helst exportera hela HACCP-journalen som PDF eller CSV.",
            "Vad kostar Mittpass HACCP-system?" to "Endast 39 kr per aktiv användare och månad. Då ingår även elektronisk personalliggare, schemaläggning och löneunderlag i samma app."
        ),
        ctaText = "Starta digitalt HACCP gratis i 30 dagar",
        onSignUp = onSignUp,
        onLogin = onLogin,
        onNavigate = onNavigate,
        onPrivacy = onPrivacy,
        onTerms = onTerms,
        onDpa = onDpa
    )
}

// ─────────────────────────────────────────
// SHARED FEATURE PAGE LAYOUT
// ─────────────────────────────────────────
@Composable
private fun featurePage(
    path: String,
    eyebrow: String,
    h1: String,
    lead: String,
    showcaseImage: String,
    showcaseAlt: String,
    benefits: List<Pair<Pair<String, String>, String>>,
    compliance: ComplianceBox,
    faqs: List<Pair<String, String>>,
    ctaText: String,
    onSignUp: () -> Unit,
    onLogin: () -> Unit,
    onNavigate: (String) -> Unit,
    onPrivacy: () -> Unit = {},
    onTerms: () -> Unit = {},
    onDpa: () -> Unit = {}
) {
    Div({
        style {
            property("min-height", "100vh")
            property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
            property("background", "var(--rp-surface)")
            property("color", "var(--rp-text)")
        }
    }) {
        LandingHeader(onLogin = onLogin, onSignUp = onSignUp, onNavigate = onNavigate)

        // Modern Light Hero
        Section({
            style {
                property("padding", "5rem 1.75rem 3.5rem")
                property("background", "var(--rp-surface)")
                property("text-align", "center")
                property("position", "relative")
                property("overflow", "hidden")
            }
        }) {
            Div({
                style {
                    property("position", "absolute")
                    property("top", "-120px")
                    property("left", "50%")
                    property("transform", "translateX(-50%)")
                    property("width", "800px")
                    property("height", "500px")
                    property("background", "radial-gradient(ellipse at center, rgba(30, 58, 138, 0.06) 0%, transparent 70%)")
                    property("pointer-events", "none")
                }
            })

            Div({
                style {
                    property("max-width", "880px")
                    property("margin", "0 auto")
                    property("position", "relative")
                    property("z-index", "2")
                }
            }) {
                Span({
                    style {
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("gap", "0.5rem")
                        property("background", "var(--rp-info-bg)")
                        property("color", "var(--rp-pine)")
                        property("font-size", "0.8rem")
                        property("font-weight", "700")
                        property("padding", "0.4rem 1rem")
                        property("border-radius", "6px")
                        property("border", "1px solid var(--rp-info-border)")
                        property("margin-bottom", "1.5rem")
                        property("letter-spacing", "0.04em")
                        property("text-transform", "uppercase")
                    }
                }) {
                    Span({
                        style {
                            property("width", "6px")
                            property("height", "6px")
                            property("border-radius", "50%")
                            property("background", "var(--rp-pine)")
                            property("display", "inline-block")
                        }
                    })
                    Text(eyebrow)
                }

                H1({
                    style {
                        property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                        property("font-size", "clamp(2.1rem, 4.2vw, 3.25rem)")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                        property("margin", "0 0 1.25rem")
                        property("line-height", "1.15")
                        property("letter-spacing", "-0.03em")
                    }
                }) {
                    Text(h1)
                }

                P({
                    style {
                        property("font-size", "clamp(1.05rem, 1.8vw, 1.2rem)")
                        property("color", "var(--rp-subtle)")
                        property("line-height", "1.7")
                        property("margin", "0 auto 2.25rem")
                        property("max-width", "740px")
                    }
                }) {
                    Text(lead)
                }

                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", "center")
                        property("gap", "1.25rem")
                        property("margin-bottom", "3.5rem")
                    }
                }) {
                    Button({
                        onClick { onSignUp() }
                        style {
                            property("display", "inline-flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("gap", "0.6rem")
                            property("padding", "0.95rem 2.25rem")
                            property("font-size", "1.05rem")
                            property("font-weight", "700")
                            property("font-family", "inherit")
                            property("background", "var(--rp-pine)")
                            property("color", "white")
                            property("border-radius", "8px")
                            property("border", "none")
                            property("cursor", "pointer")
                            property("box-shadow", "0 6px 20px rgba(30, 58, 138, 0.28)")
                            property("min-height", "52px")
                            property("transition", "all 0.2s ease")
                        }
                    }) {
                        Text(ctaText)
                        FaIcon("fa-solid fa-arrow-right", size = "0.85rem")
                    }

                    Div({
                        style {
                            property("display", "flex")
                            property("gap", "1.25rem")
                            property("flex-wrap", "wrap")
                            property("justify-content", "center")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem"); property("font-weight", "600") } }) {
                            FaIcon("fa-solid fa-shield-check", size = "0.85rem", color = "var(--rp-pine)")
                            Text("Skatteverket & Livsmedelsverket-anpassat")
                        }
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem"); property("font-weight", "600") } }) {
                            FaIcon("fa-solid fa-tag", size = "0.85rem", color = "var(--rp-pine)")
                            Text("39 kr / aktiv användare / mån")
                        }
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem"); property("font-weight", "600") } }) {
                            FaIcon("fa-solid fa-credit-card", size = "0.85rem", color = "var(--rp-pine)")
                            Text("30 dagar gratis utan betalkort")
                        }
                    }
                }

                // Showcase Image Frame
                Div({
                    style {
                        property("max-width", "1000px")
                        property("margin", "0 auto")
                        property("border-radius", "16px")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("box-shadow", "0 25px 50px -12px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(226, 232, 240, 0.6)")
                        property("overflow", "hidden")
                        property("background", "var(--rp-overlay)")
                    }
                }) {
                    Div({
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "6px")
                            property("padding", "0.75rem 1rem")
                            property("background", "var(--rp-overlay)")
                            property("border-bottom", "1px solid var(--rp-hl-med)")
                        }
                    }) {
                        Div({ style { property("width", "10px"); property("height", "10px"); property("border-radius", "50%"); property("background", "var(--rp-love)") } })
                        Div({ style { property("width", "10px"); property("height", "10px"); property("border-radius", "50%"); property("background", "var(--rp-gold)") } })
                        Div({ style { property("width", "10px"); property("height", "10px"); property("border-radius", "50%"); property("background", "var(--rp-pine)") } })
                        Span({
                            style {
                                property("margin-left", "0.75rem")
                                property("font-size", "0.75rem")
                                property("color", "var(--rp-subtle)")
                                property("font-weight", "600")
                            }
                        }) { Text("mittpass.se$path") }
                    }

                    Img(src = showcaseImage, alt = showcaseAlt) {
                        style {
                            property("width", "100%")
                            property("height", "auto")
                            property("display", "block")
                        }
                    }
                }
            }
        }

        // Benefits Grid
        Section({
            style {
                property("padding", "5rem 1.75rem")
                property("background", "var(--rp-overlay)")
                property("border-top", "1px solid var(--rp-hl-med)")
                property("border-bottom", "1px solid var(--rp-hl-med)")
            }
        }) {
            Div({ style { property("max-width", "1100px"); property("margin", "0 auto") } }) {
                Div({
                    style {
                        property("text-align", "center")
                        property("margin-bottom", "3rem")
                    }
                }) {
                    Span({
                        style {
                            property("display", "inline-block")
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("font-size", "0.8rem")
                            property("font-weight", "800")
                            property("padding", "0.35rem 0.9rem")
                            property("border-radius", "6px")
                            property("letter-spacing", "0.06em")
                            property("text-transform", "uppercase")
                            property("margin-bottom", "0.75rem")
                        }
                    }) { Text("Fördelar & Funktioner") }

                    H2({
                        style {
                            property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                            property("font-size", "clamp(1.75rem, 3.5vw, 2.35rem)")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("margin", "0")
                            property("letter-spacing", "-0.02em")
                        }
                    }) { Text("Varför restauranger, caféer och pizzerior väljer Mittpass") }
                }

                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(300px, 1fr))")
                        property("gap", "1.5rem")
                    }
                }) {
                    benefits.forEach { (iconTitle, desc) ->
                        val (icon, title) = iconTitle
                        Div({
                            style {
                                property("background", "var(--rp-surface)")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("border-radius", "16px")
                                property("padding", "2rem")
                                property("box-shadow", "0 4px 12px rgba(15, 23, 42, 0.03)")
                            }
                        }) {
                            Div({
                                style {
                                    property("width", "46px")
                                    property("height", "46px")
                                    property("border-radius", "10px")
                                    property("background", "var(--rp-info-bg)")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                    property("margin-bottom", "1.25rem")
                                }
                            }) {
                                FaIcon(icon, size = "1.2rem", color = "var(--rp-pine)")
                            }
                            H3({
                                style {
                                    property("font-size", "1.1rem")
                                    property("font-weight", "700")
                                    property("color", "var(--rp-text)")
                                    property("margin", "0 0 0.5rem")
                                    property("letter-spacing", "-0.01em")
                                }
                            }) { Text(title) }
                            P({
                                style {
                                    property("font-size", "0.92rem")
                                    property("color", "var(--rp-subtle)")
                                    property("line-height", "1.65")
                                    property("margin", "0")
                                }
                            }) { Text(desc) }
                        }
                    }
                }
            }
        }

        // Legal & Compliance Deep-Dive Section
        Section({
            style {
                property("padding", "5rem 1.75rem")
                property("background", "var(--rp-surface)")
                property("border-bottom", "1px solid var(--rp-hl-med)")
            }
        }) {
            Div({ style { property("max-width", "1040px"); property("margin", "0 auto") } }) {
                Div({
                    style {
                        property("text-align", "center")
                        property("margin-bottom", "3rem")
                    }
                }) {
                    Span({
                        style {
                            property("display", "inline-block")
                            property("background", "var(--rp-ok-bg)")
                            property("color", "var(--rp-ok-text)")
                            property("font-size", "0.8rem")
                            property("font-weight", "800")
                            property("padding", "0.35rem 0.9rem")
                            property("border-radius", "6px")
                            property("letter-spacing", "0.06em")
                            property("text-transform", "uppercase")
                            property("margin-bottom", "0.75rem")
                        }
                    }) { Text(compliance.badge) }

                    H2({
                        style {
                            property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                            property("font-size", "clamp(1.65rem, 3.2vw, 2.2rem)")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("margin", "0 0 1rem")
                            property("letter-spacing", "-0.02em")
                        }
                    }) { Text(compliance.heading) }

                    P({
                        style {
                            property("font-size", "1.02rem")
                            property("color", "var(--rp-subtle)")
                            property("line-height", "1.7")
                            property("max-width", "760px")
                            property("margin", "0 auto")
                        }
                    }) { Text(compliance.intro) }
                }

                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(240px, 1fr))")
                        property("gap", "1.25rem")
                    }
                }) {
                    compliance.facts.forEach { (factTitle, factDesc) ->
                        Div({
                            style {
                                property("background", "var(--rp-overlay)")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("border-left", "4px solid var(--rp-pine)")
                                property("border-radius", "12px")
                                property("padding", "1.5rem")
                            }
                        }) {
                            H3({
                                style {
                                    property("font-size", "1rem")
                                    property("font-weight", "800")
                                    property("color", "var(--rp-text)")
                                    property("margin", "0 0 0.5rem")
                                }
                            }) { Text(factTitle) }
                            P({
                                style {
                                    property("font-size", "0.9rem")
                                    property("color", "var(--rp-subtle)")
                                    property("line-height", "1.6")
                                    property("margin", "0")
                                }
                            }) { Text(factDesc) }
                        }
                    }
                }
            }
        }

        // Comparison Table Section (High AI-Search / GEO & Conversion value)
        Section({
            style {
                property("padding", "5rem 1.75rem")
                property("background", "var(--rp-overlay)")
                property("border-bottom", "1px solid var(--rp-hl-med)")
            }
        }) {
            Div({ style { property("max-width", "980px"); property("margin", "0 auto") } }) {
                Div({
                    style {
                        property("text-align", "center")
                        property("margin-bottom", "2.5rem")
                    }
                }) {
                    Span({
                        style {
                            property("display", "inline-block")
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("font-size", "0.8rem")
                            property("font-weight", "800")
                            property("padding", "0.35rem 0.9rem")
                            property("border-radius", "6px")
                            property("letter-spacing", "0.06em")
                            property("text-transform", "uppercase")
                            property("margin-bottom", "0.75rem")
                        }
                    }) { Text("Jämförelse") }

                    H2({
                        style {
                            property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                            property("font-size", "clamp(1.65rem, 3.2vw, 2.2rem)")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("margin", "0")
                        }
                    }) { Text("Mittpass jämfört med separata system & papperspärmar") }
                }

                Div({
                    style {
                        property("overflow-x", "auto")
                        property("background", "var(--rp-surface)")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("border-radius", "16px")
                        property("box-shadow", "0 4px 16px rgba(15, 23, 42, 0.04)")
                    }
                }) {
                    Table({
                        style {
                            property("width", "100%")
                            property("border-collapse", "collapse")
                            property("text-align", "left")
                            property("font-size", "0.92rem")
                        }
                    }) {
                        Thead {
                            Tr({ style { property("background", "var(--rp-overlay)"); property("border-bottom", "2px solid var(--rp-overlay)") } }) {
                                Th({ style { property("padding", "1rem 1.25rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Funktion / Villkor") }
                                Th({ style { property("padding", "1rem 1.25rem"); property("font-weight", "800"); property("color", "var(--rp-pine)"); property("background", "var(--rp-info-bg)") } }) { Text("Mittpass (Allt-i-ett)") }
                                Th({ style { property("padding", "1rem 1.25rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) { Text("Traditionella system") }
                                Th({ style { property("padding", "1rem 1.25rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) { Text("Pärm & Excel") }
                            }
                        }
                        Tbody {
                            val rows = listOf(
                                listOf("Månadspris", "39 kr / aktiv användare", "600 – 3 500+ kr/mån fast avgift", "0 kr (hög tidsåtgång)"),
                                listOf("Bindningstid & startavgift", "0 kr startavgift, ingen bindningstid", "Ofta 12 mån bindningstid", "Ingen"),
                                listOf("Skatteverket Personalliggare", "Ingår (SKVFS 2015:6 & CSV-export)", "Ingår eller dyrt tillval", "Hög risk för 12 500 kr i böter"),
                                listOf("Schemaläggning & Vaktbyten", "Ingår (Vecko/Månad/År + Push)", "Ingår", "Manuellt via SMS & papper"),
                                listOf("Digital Egenkontroll & HACCP", "Ingår i samma app", "Kräver separat HACCP-app", "Manuella papperslistor"),
                                listOf("Löneunderlag, OB & Fortnox", "Ingår automatiskt", "Ofta extra modulavgift", "Manuell uträkning varje månad")
                            )
                            rows.forEach { cols ->
                                Tr({ style { property("border-bottom", "1px solid var(--rp-hl-med)") } }) {
                                    Td({ style { property("padding", "0.95rem 1.25rem"); property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(cols[0]) }
                                    Td({ style { property("padding", "0.95rem 1.25rem"); property("font-weight", "700"); property("color", "var(--rp-pine)"); property("background", "rgba(239, 246, 255, 0.45)") } }) { Text(cols[1]) }
                                    Td({ style { property("padding", "0.95rem 1.25rem"); property("color", "var(--rp-subtle)") } }) { Text(cols[2]) }
                                    Td({ style { property("padding", "0.95rem 1.25rem"); property("color", "var(--rp-subtle)") } }) { Text(cols[3]) }
                                }
                            }
                        }
                    }
                }
            }
        }

        // FAQ Section
        Section({
            style {
                property("padding", "5rem 1.75rem")
                property("background", "var(--rp-surface)")
                property("border-bottom", "1px solid var(--rp-hl-med)")
            }
        }) {
            Div({ style { property("max-width", "780px"); property("margin", "0 auto") } }) {
                Div({
                    style {
                        property("text-align", "center")
                        property("margin-bottom", "3rem")
                    }
                }) {
                    Span({
                        style {
                            property("display", "inline-block")
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("font-size", "0.8rem")
                            property("font-weight", "800")
                            property("padding", "0.35rem 0.9rem")
                            property("border-radius", "6px")
                            property("letter-spacing", "0.06em")
                            property("text-transform", "uppercase")
                            property("margin-bottom", "0.75rem")
                        }
                    }) { Text("Frågor & Svar") }

                    H2({
                        style {
                            property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                            property("font-size", "clamp(1.75rem, 3.5vw, 2.35rem)")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("margin", "0")
                            property("letter-spacing", "-0.02em")
                        }
                    }) { Text("Vanliga frågor") }
                }

                faqs.forEach { (q, a) ->
                    Div({
                        style {
                            property("padding", "1.5rem 0")
                            property("border-bottom", "1px solid var(--rp-hl-med)")
                        }
                    }) {
                        Div({
                            style {
                                property("display", "flex")
                                property("align-items", "flex-start")
                                property("gap", "0.75rem")
                                property("margin-bottom", "0.5rem")
                            }
                        }) {
                            Span({
                                style {
                                    property("flex-shrink", "0")
                                    property("margin-top", "0.15rem")
                                }
                            }) { FaIcon("fa-solid fa-circle-question", size = "0.95rem", color = "var(--rp-pine)") }
                            H3({
                                style {
                                    property("font-size", "1.05rem")
                                    property("font-weight", "700")
                                    property("color", "var(--rp-text)")
                                    property("margin", "0")
                                    property("line-height", "1.4")
                                }
                            }) { Text(q) }
                        }
                        P({
                            style {
                                property("font-size", "0.95rem")
                                property("color", "var(--rp-subtle)")
                                property("line-height", "1.7")
                                property("margin", "0 0 0 1.7rem")
                            }
                        }) { Text(a) }
                    }
                }
            }
        }

        // Cross-Linking Section: Explore All Modules
        Section({
            style {
                property("padding", "4.5rem 1.75rem")
                property("background", "var(--rp-surface)")
                property("border-bottom", "1px solid var(--rp-hl-med)")
            }
        }) {
            Div({ style { property("max-width", "1100px"); property("margin", "0 auto") } }) {
                H2({
                    style {
                        property("font-size", "1.5rem")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                        property("text-align", "center")
                        property("margin", "0 0 2rem")
                    }
                }) { Text("Utforska alla delar i Mittpass verksamhetssystem") }

                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                        property("gap", "1rem")
                    }
                }) {
                    val modules = listOf(
                        Triple("Elektronisk Personalliggare", "/personalliggare", "personalliggare") to "Godkänd enligt Skatteverkets krav med mobil stämpling & CSV-export.",
                        Triple("Schemaläggning Personal", "/schemalagga-personal", "schemalagga") to "Vecko- och månadsvy, öppna pass, vaktbyten och OB-beräkning.",
                        Triple("Digital Egenkontroll", "/egenkontroll", "egenkontroll") to "Checklistor, städscheman och dagliga rutiner direkt i mobilen.",
                        Triple("Livsmedelskontroll", "/livsmedelskontroll", "livsmedelskontroll") to "Inspektionsläge och protokoll inför kommunens tillsynsbesök.",
                        Triple("HACCP & Temperaturlogg", "/haccp", "haccp") to "Kritiska styrpunkter (CCP), kyl/frys-loggar och avvikelsehantering."
                    )
                    modules.forEach { (linkInfo, summary) ->
                        val (title, url, navKey) = linkInfo
                        val isCurrent = url == path
                        A(attrs = {
                            href(url)
                            onClick { it.preventDefault(); onNavigate(navKey) }
                            style {
                                property("display", "block")
                                property("text-decoration", "none")
                                property("background", if (isCurrent) "var(--rp-info-bg)" else "var(--rp-overlay)")
                                property("border", if (isCurrent) "2px solid var(--rp-pine)" else "1px solid var(--rp-hl-med)")
                                property("border-radius", "12px")
                                property("padding", "1.25rem")
                                property("cursor", "pointer")
                            }
                        }) {
                            H3({
                                style {
                                    property("font-size", "0.98rem")
                                    property("font-weight", "800")
                                    property("color", "var(--rp-pine)")
                                    property("margin", "0 0 0.4rem")
                                }
                            }) { Text(title) }
                            P({
                                style {
                                    property("font-size", "0.84rem")
                                    property("color", "var(--rp-subtle)")
                                    property("line-height", "1.5")
                                    property("margin", "0")
                                }
                            }) { Text(summary) }
                        }
                    }
                }
            }
        }

        // Bottom CTA Section
        Section({
            style {
                property("padding", "5rem 1.75rem")
                property("background", "var(--rp-overlay)")
                property("text-align", "center")
            }
        }) {
            Div({
                style {
                    property("max-width", "680px")
                    property("margin", "0 auto")
                }
            }) {
                H2({
                    style {
                        property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                        property("font-size", "clamp(1.75rem, 3.5vw, 2.35rem)")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                        property("margin", "0 0 1rem")
                        property("letter-spacing", "-0.02em")
                    }
                }) { Text("Redo att förenkla driften med Mittpass?") }

                P({
                    style {
                        property("font-size", "1.1rem")
                        property("color", "var(--rp-subtle)")
                        property("line-height", "1.6")
                        property("margin", "0 0 2rem")
                    }
                }) {
                    Text("Kom igång på under två minuter. 30 dagar helt gratis – inga bindningstider eller dolda avgifter.")
                }

                Button({
                    onClick { onSignUp() }
                    style {
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("gap", "0.6rem")
                        property("padding", "0.95rem 2.25rem")
                        property("font-size", "1.05rem")
                        property("font-weight", "700")
                        property("font-family", "inherit")
                        property("background", "var(--rp-pine)")
                        property("color", "white")
                        property("border-radius", "8px")
                        property("border", "none")
                        property("cursor", "pointer")
                        property("box-shadow", "0 6px 20px rgba(30, 58, 138, 0.28)")
                        property("min-height", "52px")
                    }
                }) {
                    Text(ctaText)
                    FaIcon("fa-solid fa-arrow-right", size = "0.85rem")
                }
            }
        }

        LandingFooter(
            onPrivacy = onPrivacy,
            onTerms = onTerms,
            onDpa = onDpa,
            onDeleteAccount = { onNavigate("delete-account") },
            onSupport = { onNavigate("support") },
            onNavigate = onNavigate
        )
    }
}
